import React, { useEffect, useMemo, useState } from "react";
import { Input } from "antd";
import PreviewQuestionLabel from "../../../common/PreviewQuestionLabel/PreviewQuestionLabel";
import { useSurveyInputStore } from "../../../../surveyStore";
import PreviewQuestionConstraints from "../../../common/PreviewQuestionConstraints/PreviewQuestionConstraints";
import useEvaluateChoiceFilter from "../../question-preview/hooks/useEvaluateChoiceFilter";

import "./Matrix.scss";

const { TextArea } = Input;

export const Matrix = React.memo(
   ({ props, hasErrors, surveyId, userId, submissionId, label, hint, language }) => {
      const { id, selectQuestionChoices, secondChoices, questionType, isRequired } = props;

      const hasSubmissionId = Boolean(submissionId);

      const [value, setValue] = useState(
         hasSubmissionId
            ? props.answers.find((answer) =>
                 props.repeatInstanceIndex
                    ? answer?.answerDetail.repeatGroup === props.repeatInstanceIndex
                    : answer
              )?.answerDetail?.answerMatrices || []
            : []
      );

      const inputState = useSurveyInputStore((state) => state.inputs?.[id]);
      const setInputs = useSurveyInputStore((state) => state.setInputs);

      const showErrors =
         hasErrors &&
         isRequired &&
         (!inputState || inputState?.answerDetail?.answerMatrix?.length === 0);

      // Rows come from a choice list like any select, so a `choice_filter` on the
      // question has to narrow them the same way it narrows select options. A
      // submission keeps every row: its filter inputs are no longer editable.
      const visibleRows = useEvaluateChoiceFilter({
         choiceFilter: hasSubmissionId ? null : props.choiceFilter,
         name: props.name,
         selectQuestionChoices,
         repeatInstanceIndex: props.repeatInstanceIndex,
         isRepeat: !!props.repeatInstanceIndex
      });

      const rowQuestions = useMemo(
         () =>
            visibleRows.map((element) => {
               return {
                  choice:
                     element.choiceLabels?.find((choice) => choice.languageId === language)?.text ??
                     element.choiceLabels?.[0]?.text,
                  id: element.id
               };
            }),
         [visibleRows, language]
      );

      const columnQuestions = useMemo(
         () =>
            secondChoices.map((element) => {
               return {
                  // Older second choices were saved without labels, so fall back to the
                  // untranslated `name` rather than rendering an empty column header
                  choice:
                     element.choiceLabels?.find((choice) => choice.languageId === language)?.text ??
                     element.choiceLabels?.[0]?.text ??
                     element.name,
                  id: element.id
               };
            }),
         [secondChoices, language]
      );

      const onChange = (event) => {
         const { name, value: inputValue } = event.target;
         const splitString = name.split("===");
         const choiceId = splitString[0];
         const secondChoiceId = splitString[1];

         const existingIndex = value.findIndex(
            (el) => el.choiceId === choiceId && el.secondChoiceId === secondChoiceId
         );

         if (inputValue === "") {
            if (existingIndex !== -1) {
               setValue((prev) => {
                  const updatedValue = [...prev];
                  updatedValue.splice(existingIndex, 1);
                  return updatedValue;
               });
            }
         } else {
            if (existingIndex !== -1) {
               setValue((prev) => {
                  const updatedValue = [...prev];
                  updatedValue[existingIndex] = { choiceId, secondChoiceId, text: inputValue };
                  return updatedValue;
               });
            } else {
               setValue((prev) => [...prev, { choiceId, secondChoiceId, text: inputValue }]);
            }
         }
      };

      useEffect(() => {
         if (hasSubmissionId) {
            return;
         }

         setValue(inputState?.answerDetail?.answerMatrix ?? []);
      }, [inputState, id, hasSubmissionId]);

      // A row the filter just hid must not keep its text: the respondent can no
      // longer see or change it, so it would be submitted behind their back.
      useEffect(() => {
         if (hasSubmissionId || !props.choiceFilter || value.length === 0) {
            return;
         }

         const visibleRowIds = new Set(visibleRows.map((row) => row.id));
         const keptCells = value.filter((cell) => visibleRowIds.has(cell.choiceId));

         if (keptCells.length === value.length) {
            return;
         }

         setValue(keptCells);

         if (keptCells.length === 0) {
            // The effect below bails out on an empty list, so clear the answer here.
            setInputs({ target: { name: id, value: "" } });
         }
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [visibleRows, value, hasSubmissionId, props.choiceFilter]);

      useEffect(() => {
         if (hasSubmissionId) {
            return;
         }
         if (value.length === 0) {
            return;
         }
         setInputs({
            target: {
               name: id,
               value: {
                  surveyId: surveyId,
                  userId: userId,
                  questionId: id,
                  questionType: questionType,
                  name: props.name,
                  skipLogicAnswer: value,
                  answerDetail: {
                     answerMatrix: value
                  }
               }
            }
         });
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [value, hasSubmissionId]);

      return (
         <div className="matrix-submission-type">
            <PreviewQuestionLabel label={label} isRequired={isRequired} hint={hint} />
            <PreviewQuestionConstraints constraintMessages={props.constraintMessages} />

            <div className="matrix-submission-type--wrapper">
               <table className="matrix-submission-type--wrapper-table">
                  <thead>
                     <tr>
                        {[{}, ...columnQuestions].map((column, index) => (
                           <th
                              key={`column-${index}`}
                              style={{
                                 width: `${100 / (columnQuestions.length + 1)}%`,
                                 minWidth: "200px"
                              }}
                           >
                              {column.choice}
                           </th>
                        ))}
                     </tr>
                  </thead>
                  <tbody className={showErrors ? "error" : ""}>
                     {rowQuestions.map((row, index) => (
                        <tr key={index} className={showErrors ? "error" : ""}>
                           <td className={showErrors ? "error" : ""} style={{ minWidth: "200px" }}>
                              {row.choice}
                           </td>

                           {columnQuestions.map((column, columnIndex) => (
                              <td
                                 key={`row-${index}-column-${columnIndex}`}
                                 className={showErrors ? "error" : ""}
                              >
                                 <TextArea
                                    autoSize={{ minRows: 4 }}
                                    onChange={onChange}
                                    name={`${row.id}===${column.id}`}
                                    placeholder="Type here..."
                                    value={
                                       value.find(
                                          (el) =>
                                             el.choiceId === row.id &&
                                             el.secondChoiceId === column.id
                                       )?.text
                                    }
                                    readOnly={hasSubmissionId}
                                    prefixCls="matrix-submission-type--wrapper-table_textarea"
                                 />
                              </td>
                           ))}
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>

            <span className="rating-submission-type--error">
               {showErrors ? "This field is required" : " "}
            </span>
         </div>
      );
   }
);
